/**
 * 노후화 분석 — 공개 API 방안 1·2·3 (DESIGN §8.11.4).
 *
 * 세 위젯 모두 **빌드가 사전 계산한 전국 값**이라 좌측 필터를 타지 않는다. 층 통제는
 * 모집단 전체의 구조(사업자 × 타입 × 제조사)를 써야 성립하기 때문이다.
 */

import type { EChartsOption } from 'echarts'
import { useMemo, useState } from 'react'

import { ChartFrame } from '@/components/ChartFrame'
import { EChart } from '@/components/EChart'
import { Headline } from '@/components/WidgetShell'
import { axisStyle, CHART_H } from '@/lib/chart'
import { cssVar } from '@/lib/cssvar'
import { int, pct } from '@/lib/fmt'
import type { AgingEffect, AgingPublicData, Envelope } from '@/lib/types'
import { useAgingPublic } from './queries'

const EFFECT_ROWS = [
  { mode: 'standard', outcome: 'down_events', label: '고장 발생률', sub: '통신 포함' },
  { mode: 'strict', outcome: 'down_events', label: '고장 발생률', sub: '설비 고장만' },
  { mode: 'standard', outcome: 'na_share', label: '미관측 시간 비율', sub: '방안 3 · 통신두절' },
] as const

const times = (v: number) => `${v.toFixed(3)}배`

/** CI 가 1 을 걸치면 "연식 효과가 있다"고 말할 수 없다 */
const crossesOne = (e: AgingEffect) => e.lo <= 1 && e.hi >= 1

/**
 * 방안 1·3 — 연식 1년당 발생률 배수, 통제 전·후.
 *
 * **차이 자체가 메시지다.** 통제하지 않은 숫자만 보이면 사업자·제조사 구성 차이를 노후로
 * 읽게 된다. 그래서 같은 셀에서 잰 두 값을 한 줄에 겹친다 (서버가 표본을 고정한다).
 * 숲 그림(forest plot)을 HTML 로 그리는 이유: 행 6개짜리라 캔버스가 필요 없고, 표와 같은
 * 마크업이라 스크린리더가 값을 그대로 읽는다.
 */
export function AgeEffectForest() {
  const q = useAgingPublic()
  const env = q.data as Envelope<AgingPublicData> | undefined
  const effects = env?.data.effects ?? []

  const rows = EFFECT_ROWS.map((r) => ({
    ...r,
    raw: effects.find((e) => e.mode === r.mode && e.outcome === r.outcome && e.spec === 'raw'),
    strat: effects.find(
      (e) => e.mode === r.mode && e.outcome === r.outcome && e.spec === 'stratified',
    ),
  }))
  const all = effects.flatMap((e) => [e.lo, e.hi])
  const lo = Math.min(0.9, ...all)
  const hi = Math.max(1.1, ...all)
  const x = (v: number) =>
    `${((Math.log(v) - Math.log(lo)) / (Math.log(hi) - Math.log(lo))) * 100}%`
  const ticks = [0.9, 1, 1.1, 1.2, 1.3, 1.4].filter((t) => t >= lo && t <= hi)
  const main = rows[0]?.strat

  return (
    <ChartFrame
      title="연식 1년당 발생률 배수 — 통제 전·후"
      question="연식 효과처럼 보이는 것이 사업자·타입·제조사를 고정해도 남는가"
      meta={env?.meta}
      hideCoverage
      headline={
        main ? (
          <Headline
            value={times(main.irr)}
            unit={crossesOne(main) ? '통제 후 · 유의하지 않음' : '통제 후'}
            tone={crossesOne(main) ? 'neutral' : 'warn'}
          />
        ) : undefined
      }
      legend={[
        { label: '통제 없음', color: cssVar('--c-cat-2', '#a35200'), pattern: 'ring' },
        { label: '층 통제 (사업자×타입×제조사)', color: cssVar('--c-cat-1', '#0066cc'), pattern: 'dot' },
      ]}
      encoding={[
        { role: '가로축', what: '연식 1년당 배수 (로그 눈금) · 1.0 = 효과 없음' },
        { role: '가로선', what: '95% 신뢰구간' },
      ]}
      reading="같은 사업자·충전기 타입·제조사 안에서 연식이 한 해 오래될 때 발생률이 몇 배가 되는지입니다. 통제 없는 값(빈 원)과 통제한 값(찬 원)의 차이가 곧 '구성 차이를 노후로 읽은 몫'입니다."
      isLoading={q.isLoading}
      error={q.error as Error | null}
      isEmpty={!q.isLoading && effects.length === 0}
      emptyText="연식 효과가 아직 계산되지 않았습니다 — 이 기능이 들어간 뒤의 빌드가 필요합니다."
      onRetry={() => void q.refetch()}
      notes={[
        {
          label: '모형.',
          text: '셀 = (층, 연식), 층 = 사업자 × 충전기 타입 × 제조사. 연식이 둘 이상인 층만 씁니다 — 한 연식뿐인 층은 연식 효과에 정보가 없습니다. 노출 오프셋 포아송, 셀 단위 강건 표준오차(HC0). 나이 신호가 아닌 연식(한 사업자 독점)은 뺍니다.',
        },
        {
          label: '결과변수가 고장 사건은 아니다.',
          text: '공개 API 의 DOWN 은 상태 표시이지 고장 사건이 아닙니다. 고장코드가 없어 설비 기인 여부를 가를 수 없습니다 — 그래서 아래 환경공단 원천이 필요합니다.',
        },
        {
          label: '이용률을 통제하지 않는다.',
          text: '고장이 잦으면 덜 쓰이므로 이용률은 고장의 결과이기도 합니다(역인과). 통제 변수로 넣으면 연식 효과를 지웁니다.',
        },
      ]}
      table={
        <div className="ctable" tabIndex={0} role="region" aria-label="연식 효과 통제 전·후 표">
          <table className="data-table">
            <caption className="sr-only">연식 효과 통제 전·후</caption>
            <thead>
              <tr>
                <th scope="col">결과</th>
                <th scope="col">통제</th>
                <th scope="col">배수</th>
                <th scope="col">95% 구간</th>
                <th scope="col">셀</th>
                <th scope="col">층</th>
                <th scope="col">충전기</th>
              </tr>
            </thead>
            <tbody>
              {rows.flatMap((r) =>
                [r.raw, r.strat].filter(Boolean).map((e) => (
                  <tr key={`${r.mode}${r.outcome}${e!.spec}`}>
                    <th scope="row">
                      {r.label} · {r.sub}
                    </th>
                    <td>{e!.spec === 'raw' ? '없음' : '층 통제'}</td>
                    <td className="num">{times(e!.irr)}</td>
                    <td className="num">
                      {e!.lo.toFixed(3)} ~ {e!.hi.toFixed(3)}
                    </td>
                    <td className="num">{int(e!.n_cells)}</td>
                    <td className="num">{int(e!.n_strata)}</td>
                    <td className="num">{int(e!.n_chargers)}</td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        </div>
      }
    >
      <div className="forest" role="img" aria-label="연식 효과 통제 전후 숲 그림 — 값은 표 보기에 있습니다">
        <div className="forest__axis" aria-hidden="true">
          {ticks.map((t) => (
            <span
              key={t}
              className={`forest__tick${t === 1 ? ' forest__tick--one' : ''}`}
              style={{ left: x(t) }}
            >
              {t.toFixed(1)}
            </span>
          ))}
        </div>
        {rows.map((r) => (
          <div key={`${r.mode}${r.outcome}`} className="forest__row">
            <div className="forest__label">
              {r.label}
              <span className="forest__sub">{r.sub}</span>
            </div>
            <div className="forest__plot">
              <span className="forest__one" style={{ left: x(1) }} aria-hidden="true" />
              {[r.raw, r.strat].map(
                (e, i) =>
                  e && (
                    <div
                      key={e.spec}
                      className={`forest__mark forest__mark--${e.spec}`}
                      style={{ top: i === 0 ? '30%' : '70%' }}
                    >
                      <span
                        className="forest__ci"
                        style={{ left: x(e.lo), width: `calc(${x(e.hi)} - ${x(e.lo)})` }}
                      />
                      <span className="forest__dot" style={{ left: x(e.irr) }} />
                    </div>
                  ),
              )}
            </div>
            <div className="forest__vals num">
              {r.raw ? times(r.raw.irr) : '—'} → <strong>{r.strat ? times(r.strat.irr) : '—'}</strong>
              {r.strat && crossesOne(r.strat) && <span className="forest__ns">유의하지 않음</span>}
            </div>
          </div>
        ))}
      </div>
    </ChartFrame>
  )
}

type YearMetric = 'events' | 'na'

/** 방안 1·3 의 원자료 — 연식별 단순 비교. 통제 전 숫자가 무엇이었는지를 그대로 보인다. */
export function YearRates() {
  const q = useAgingPublic()
  const env = q.data as Envelope<AgingPublicData> | undefined
  const [metric, setMetric] = useState<YearMetric>('events')
  const std = useMemo(() => env?.data.cohorts.standard ?? [], [env])
  const strict = useMemo(() => env?.data.cohorts.strict ?? [], [env])
  const usable = useMemo(() => std.filter((r) => r.usable), [std])
  const held = std.filter((r) => !r.usable)

  const option = useMemo<EChartsOption>(() => {
    const c1 = cssVar('--c-cat-1', '#0066cc')
    const c2 = cssVar('--c-cat-2', '#a35200')
    const ink3 = cssVar('--c-ink-3', '#626266')
    const hairline = cssVar('--c-hairline', '#e0e0e0')
    const years = usable.map((r) => String(r.year))
    const strictBy = new Map(strict.map((r) => [r.year, r]))
    const series: EChartsOption['series'] =
      metric === 'events'
        ? [
            {
              name: '통신 포함',
              type: 'line',
              data: usable.map((r) => r.events_per_1000h),
              lineStyle: { width: 2, color: c1 },
              itemStyle: { color: c1 },
              symbol: 'circle',
              symbolSize: 8,
            },
            {
              name: '설비 고장만',
              type: 'line',
              data: usable.map((r) => strictBy.get(r.year)?.events_per_1000h ?? null),
              lineStyle: { width: 2, color: c2, type: 'dashed' },
              itemStyle: { color: c2 },
              symbol: 'diamond',
              symbolSize: 9,
            },
          ]
        : [
            {
              name: '미관측 비율',
              type: 'line',
              data: usable.map((r) => r.na_share),
              lineStyle: { width: 2, color: c1 },
              itemStyle: { color: c1 },
              symbol: 'circle',
              symbolSize: 8,
            },
          ]
    return {
      grid: { left: 56, right: 16, top: 24, bottom: 40 },
      tooltip: {
        trigger: 'axis',
        valueFormatter: (v) =>
          v == null ? '—' : metric === 'events' ? Number(v).toFixed(2) : pct(Number(v), 1),
      },
      xAxis: {
        type: 'category',
        data: years,
        name: '설치 연식',
        nameLocation: 'middle',
        nameGap: 26,
        nameTextStyle: { fontSize: 10, color: ink3 },
        ...axisStyle(ink3, hairline),
      },
      yAxis: {
        type: 'value',
        ...axisStyle(ink3, hairline),
        axisLabel: {
          fontSize: 10,
          color: ink3,
          formatter: (v: number) => (metric === 'events' ? v.toFixed(1) : `${Math.round(v * 100)}%`),
        },
      },
      series,
    }
  }, [usable, strict, metric])

  return (
    <ChartFrame
      title="연식별 단순 비교 (통제 전)"
      question="통제 전에는 어떻게 보였나 — 단조로운가"
      meta={env?.meta}
      hideCoverage
      legend={
        metric === 'events'
          ? [
              { label: '통신 포함', color: cssVar('--c-cat-1', '#0066cc'), pattern: 'dot' },
              { label: '설비 고장만', color: cssVar('--c-cat-2', '#a35200'), pattern: 'line' },
            ]
          : []
      }
      controls={
        <div className="det__seg" role="group" aria-label="지표">
          {(
            [
              ['events', '관측 1,000시간당 DOWN 세션'],
              ['na', '미관측 시간 비율'],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              className={`det__segbtn${metric === k ? ' is-on' : ''}`}
              aria-pressed={metric === k}
              onClick={() => setMetric(k)}
            >
              {label}
            </button>
          ))}
        </div>
      }
      encoding={[
        { role: '가로축', what: '설치 연식 (나이 신호로 쓸 수 있는 연식만)' },
        {
          role: '세로축',
          what: metric === 'events' ? '관측 1,000시간당 DOWN 세션 수' : '전체 시간 중 미관측(NA) 비율',
        },
      ]}
      reading="통제하지 않은 연식별 값입니다. 오래될수록 나빠지는 단조 패턴이 아니라면, 연식은 노후의 대리변수가 아니라 구성(사업자·제조사·시대)의 대리변수입니다."
      isLoading={q.isLoading}
      error={q.error as Error | null}
      isEmpty={!q.isLoading && usable.length === 0}
      emptyText="연식별 집계가 아직 없습니다 — 이 기능이 들어간 뒤의 빌드가 필요합니다."
      onRetry={() => void q.refetch()}
      notes={[
        held.length > 0
          ? {
              label: '빠진 연식.',
              text: `${held.map((r) => r.year).join('·')}년은 한 사업자 독점이거나 30기 미만이라 나이 신호로 쓰지 않습니다(연식 비교 화면의 별도 관리와 같은 규칙). 값은 표 보기에 있습니다.`,
            }
          : '',
      ]}
      table={
        <div className="ctable" tabIndex={0} role="region" aria-label="연식별 발생률과 미관측 비율 표">
          <table className="data-table">
            <caption className="sr-only">연식별 발생률과 미관측 비율</caption>
            <thead>
              <tr>
                <th scope="col">연식</th>
                <th scope="col">충전기</th>
                <th scope="col">DOWN/1,000h 통신 포함</th>
                <th scope="col">DOWN/1,000h 설비만</th>
                <th scope="col">미관측 비율</th>
                <th scope="col">나이 신호</th>
              </tr>
            </thead>
            <tbody>
              {std.map((r) => {
                const s = strict.find((x) => x.year === r.year)
                return (
                  <tr key={r.year} className={r.usable ? '' : 'is-muted'}>
                    <th scope="row">{r.year}</th>
                    <td className="num">{int(r.n_chargers)}</td>
                    <td className="num">{r.events_per_1000h?.toFixed(2) ?? '—'}</td>
                    <td className="num">{s?.events_per_1000h?.toFixed(2) ?? '—'}</td>
                    <td className="num">{pct(r.na_share, 1)}</td>
                    <td>{r.usable ? '씀' : '별도 관리'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      }
    >
      <EChart option={option} height={CHART_H} ariaLabel="연식별 발생률 꺾은선" />
    </ChartFrame>
  )
}

/**
 * 방안 2(공개) — 스냅샷에서 사라진 충전기 (DESIGN §4.6).
 *
 * 공개 데이터에서 **가장 강한 노후 신호**였다. 다만 사라짐에는 사유가 없다 — 한 사업자의
 * 일괄 정리가 연식 신호처럼 보일 수 있어, 연식마다 최다 사업자의 몫을 함께 싣는다.
 */
export function Removal() {
  const q = useAgingPublic()
  const env = q.data as Envelope<AgingPublicData> | undefined
  const d = env?.data
  const rows = useMemo(
    () => (d?.removal ?? []).filter((r) => r.usable && r.n_seen >= 30),
    [d],
  )
  const snaps = d?.snapshots

  const option = useMemo<EChartsOption>(() => {
    const c1 = cssVar('--c-cat-1', '#0066cc')
    const ink3 = cssVar('--c-ink-3', '#626266')
    const hairline = cssVar('--c-hairline', '#e0e0e0')
    return {
      grid: { left: 56, right: 16, top: 28, bottom: 40 },
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          const arr = Array.isArray(params) ? params : [params]
          const r = rows[(arr[0] as { dataIndex: number }).dataIndex]
          if (!r) return ''
          return `<b>${r.year}년산</b><br/>사라짐 ${int(r.removed)} / ${int(r.n_seen)}기 (${pct(
            r.removed_rate,
            1,
          )})<br/>최다 사업자 ${r.top_busi ?? '—'} ${pct(r.top_busi_share, 0)}`
        },
      },
      xAxis: {
        type: 'category',
        data: rows.map((r) => String(r.year)),
        name: '설치 연식',
        nameLocation: 'middle',
        nameGap: 26,
        nameTextStyle: { fontSize: 10, color: ink3 },
        ...axisStyle(ink3, hairline),
      },
      yAxis: {
        type: 'value',
        ...axisStyle(ink3, hairline),
        axisLabel: { fontSize: 10, color: ink3, formatter: (v: number) => `${(v * 100).toFixed(0)}%` },
      },
      series: [
        {
          type: 'bar',
          barCategoryGap: '24%',
          data: rows.map((r) => ({
            value: r.removed_rate,
            itemStyle: {
              color: c1,
              borderRadius: [4, 4, 0, 0],
              // 한 사업자가 절반 이상이면 빗금 — 연식 신호가 아니라 그 사업자의 정리일 수 있다
              decal: r.dominated
                ? { symbol: 'rect', dashArrayX: [1, 0], dashArrayY: [2, 4], rotation: Math.PI / 4, color: 'rgba(255,255,255,0.55)' }
                : undefined,
            },
            label: r.dominated
              ? { show: true, position: 'top', fontSize: 10, color: ink3, formatter: `${r.top_busi} ${pct(r.top_busi_share, 0)}` }
              : undefined,
          })),
        },
      ],
    }
  }, [rows])

  const best = rows.reduce<(typeof rows)[number] | null>(
    (a, r) => (a == null || (r.removed_rate ?? 0) > (a.removed_rate ?? 0) ? r : a),
    null,
  )

  return (
    <ChartFrame
      title="연식별 사라짐률"
      question="오래된 연식이 더 많이 목록에서 사라지는가 — 그것은 누구의 정리인가"
      meta={env?.meta}
      hideCoverage
      headline={
        best ? <Headline value={pct(best.removed_rate, 1)} unit={`최고 · ${best.year}년산`} /> : undefined
      }
      legend={[
        { label: '여러 사업자', color: cssVar('--c-cat-1', '#0066cc') },
        { label: `한 사업자 ≥ ${pct(d?.dominant_share ?? 0.5, 0)}`, color: cssVar('--c-cat-1', '#0066cc'), pattern: 'hatch' },
      ]}
      encoding={[
        { role: '가로축', what: '설치 연식 (마지막으로 본 값)' },
        { role: '세로축', what: '관측 기간 중 사라진 비율' },
      ]}
      reading={
        snaps
          ? `일 스냅샷 ${snaps.n}개(${snaps.first} ~ ${snaps.last})에서 처음 본 충전기 중, 마지막 ${snaps.absent_runs}개 스냅샷에 연속으로 없는 것을 '사라짐'으로 셉니다.`
          : ''
      }
      isLoading={q.isLoading}
      error={q.error as Error | null}
      isEmpty={!q.isLoading && rows.length === 0}
      emptyText="스냅샷 이력이 아직 없습니다 — 이 기능이 들어간 뒤의 빌드가 필요합니다."
      onRetry={() => void q.refetch()}
      notes={[
        {
          label: '사라짐은 철거 사유를 모른다.',
          text: '공개 API 에는 철거 사유가 없습니다. 노후 철거와 사업 종료·부지 반환·고출력 전환이 같은 모양으로 사라집니다 — 이 막대를 수명 종료율로 읽으면 과대추정입니다.',
        },
        {
          label: '빗금 막대.',
          text: '그 연식의 사라짐 절반 이상이 한 사업자입니다. 연식 신호라기보다 그 사업자의 일괄 정리일 수 있습니다.',
        },
        {
          label: '연식은 마지막 값.',
          text: '같은 ID 에서 연식이 바뀐 충전기가 있습니다(표의 속성 변경). 마지막으로 본 연식으로 묶습니다.',
        },
      ]}
      table={
        <div className="ctable" tabIndex={0} role="region" aria-label="연식별 사라짐·삭제표시·속성 변경 표">
          <table className="data-table">
            <caption className="sr-only">연식별 사라짐·삭제표시·속성 변경</caption>
            <thead>
              <tr>
                <th scope="col">연식</th>
                <th scope="col">본 충전기</th>
                <th scope="col">사라짐</th>
                <th scope="col">사라짐률</th>
                <th scope="col">최다 사업자</th>
                <th scope="col">삭제표시(delYn)</th>
                <th scope="col">속성 변경</th>
                <th scope="col">신규 등장</th>
              </tr>
            </thead>
            <tbody>
              {(d?.removal ?? []).map((r) => (
                <tr key={r.year} className={r.usable ? '' : 'is-muted'}>
                  <th scope="row">{r.year}</th>
                  <td className="num">{int(r.n_seen)}</td>
                  <td className="num">{int(r.removed)}</td>
                  <td className="num">{pct(r.removed_rate, 1)}</td>
                  <td>
                    {r.top_busi ?? '—'} {r.top_busi_share != null && pct(r.top_busi_share, 0)}
                  </td>
                  <td className="num">{int(r.del_flag_ever)}</td>
                  <td className="num">{int(r.attr_changed)}</td>
                  <td className="num">{int(r.new_after_start)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }
    >
      <EChart option={option} height={CHART_H} ariaLabel="연식별 사라짐률 막대" />
      {(d?.removal_busi.length ?? 0) > 0 && (
        <details className="det__more">
          <summary>사라짐이 많은 사업자 {d!.removal_busi.length}곳</summary>
          <ul className="det__busi">
            {d!.removal_busi.map((b) => (
              <li key={b.busi_id}>
                <span>{b.busi_nm}</span>
                <span className="num">
                  {int(b.removed)} / {int(b.n_seen)}기
                  {b.removed_year_median != null && ` · 사라진 것의 연식 중앙값 ${b.removed_year_median}`}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </ChartFrame>
  )
}
